// src/components/NotificationPanel/NotificationPanel.jsx
import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Typography,
  Chip,
  Button,
  Divider,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  CheckCircle as ReadIcon,
  CheckCircle, // ✅ ДОДАНО
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  TrendingUp,
  WaterDrop,
  LocalFireDepartment,
  Build,
  Warning
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/i18n';

const NotificationIcon = ({ type }) => {
  const icons = {
    efficiency_alert: <TrendingUp color="warning" />,
    resource_alert: <WaterDrop color="info" />,
    production_target: <CheckCircle color="success" />,
    maintenance_reminder: <Build color="action" />,
    anomaly_detected: <Warning color="error" />
  };
  return icons[type] || <NotificationsIcon />;
};

const PriorityChip = ({ priority, t }) => {
  const config = {
    critical: { color: 'error', label: t('ai_priority_critical') },
    high: { color: 'error', label: t('ai_priority_high') },
    medium: { color: 'warning', label: t('ai_priority_medium') },
    low: { color: 'info', label: t('ai_priority_low') },
    info: { color: 'success', label: t('ai_priority_info') }
  };

  const { color, label } = config[priority] || config.info;

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant="outlined"
    />
  );
};

const NotificationItem = ({ notification, onMarkRead, onDelete, t }) => {
  const formatTime = (timestamp) => {
    const now = new Date();
    const diffMs = now - new Date(timestamp);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('common_just_now');
    if (diffMins < 60) return t('time_minutes_ago', { minutes: diffMins });
    if (diffHours < 24) return t('time_hours_ago', { hours: diffHours });
    return t('time_days_ago', { days: diffDays });
  };

  return (
    <ListItem
      sx={{
        backgroundColor: notification.read ? 'transparent' : 'action.hover',
        borderLeft: notification.read ? 'none' : `4px solid ${getPriorityColor(notification.priority)}`,
        mb: 1,
        borderRadius: 1
      }}
    >
      <ListItemIcon>
        <NotificationIcon type={notification.type} />
      </ListItemIcon>
      
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {notification.title}
            </Typography>
            <PriorityChip priority={notification.priority} t={t} />
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {notification.message}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {formatTime(notification.timestamp)}
            </Typography>
          </Box>
        }
      />
      
      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!notification.read && (
            <IconButton
              size="small"
              onClick={() => onMarkRead(notification.id)}
              title={t('actions_mark_read')}
            >
              <ReadIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => onDelete(notification.id)}
            title={t('actions_delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const NotificationSettings = ({ open, anchorEl, onClose, settings, onSettingsChange, t }) => {
  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{ sx: { width: 320, p: 2 } }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('notification_settings')}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={settings.pushNotifications}
            onChange={(e) => onSettingsChange({ pushNotifications: e.target.checked })}
          />
        }
        label={t('push_notifications')}
        sx={{ width: '100%', mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={settings.emailNotifications}
            onChange={(e) => onSettingsChange({ emailNotifications: e.target.checked })}
          />
        }
        label={t('email_notifications')}
        sx={{ width: '100%', mb: 1 }}
      />

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {t('notification_types')}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={settings.efficiencyAlerts}
            onChange={(e) => onSettingsChange({ efficiencyAlerts: e.target.checked })}
          />
        }
        label={t('efficiency_alerts')}
        sx={{ width: '100%', mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={settings.resourceAlerts}
            onChange={(e) => onSettingsChange({ resourceAlerts: e.target.checked })}
          />
        }
        label={t('resource_alerts')}
        sx={{ width: '100%', mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={settings.anomalyAlerts}
            onChange={(e) => onSettingsChange({ anomalyAlerts: e.target.checked })}
          />
        }
        label={t('anomaly_alerts')}
        sx={{ width: '100%', mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={settings.maintenanceAlerts}
            onChange={(e) => onSettingsChange({ maintenanceAlerts: e.target.checked })}
          />
        }
        label={t('maintenance_alerts')}
        sx={{ width: '100%', mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={settings.achievementAlerts}
            onChange={(e) => onSettingsChange({ achievementAlerts: e.target.checked })}
          />
        }
        label={t('achievement_alerts')}
        sx={{ width: '100%', mb: 1 }}
      />
    </Menu>
  );
};

const NotificationPanel = ({ open, onClose }) => {
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const { t } = useI18n();
  
  const {
    notifications,
    unreadCount,
    settings,
    markAsRead,
    deleteNotification,
    markAllRead,
    clearAll,
    updateSettings
  } = useNotifications();

  const handleSettingsOpen = (event) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleSettingsChange = (newSettings) => {
    updateSettings(newSettings);
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const criticalNotifications = notifications.filter(n => 
    n.priority === 'critical' && !n.read
  );

  return (
    <>
      {/* Панель сповіщень */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 400, maxWidth: '90vw' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('notifications')}
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                color="primary" 
                sx={{ ml: 1 }} 
              />
            )}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleSettingsOpen}
              title={t('notification_settings')}
            >
              <SettingsIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              title={t('common_close')}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Дії */}
        {notifications.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Button
              size="small"
              onClick={markAllRead}
              disabled={unreadCount === 0}
            >
              {t('mark_all_read')}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={clearAll}
            >
              {t('clear_all')}
            </Button>
          </Box>
        )}

        {/* Список сповіщень */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                {t('no_notifications')}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 1 }}>
              {notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={markAsRead}
                  onDelete={deleteNotification}
                  t={t}
                />
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Налаштування сповіщень */}
      <NotificationSettings
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={handleSettingsClose}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        t={t}
      />
    </>
  );
};

// Допоміжна функція
function getPriorityColor(priority) {
  const colors = {
    critical: '#f44336',
    high: '#ff9800', 
    medium: '#ffc107',
    low: '#2196f3',
    info: '#4caf50'
  };
  return colors[priority] || '#757575';
}

export default NotificationPanel;